import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'

import { observer } from 'mobx-react'

import createComputed from 'modules/core/hooks/createComputed'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { amountToNumber } from 'modules/core/lib/formatCurrencyUtils'
import FiltersForm from 'modules/core/lib/forms/Filters'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import BlankSlate from 'modules/payroll_bundle/components/BlankSlate'
import { usePayrollBundlePageContext } from 'modules/payroll_bundle/components/PayrollBundle'
import getUserByItem from 'modules/payroll_bundle/components/common/helper/getUserByItem'
import orderItems from 'modules/payroll_bundle/components/common/helper/orderItems'
import { useDraftPaymentItems } from 'modules/payroll_bundle/hooks/useDraftPaymentItems'
import { RESET_DATA, UPDATE_DATA } from 'modules/payroll_bundle/lib/constants'
import { usePayrollPayments } from 'modules/payroll_bundle/lib/payrollPaymentsContext'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import { PaymentItems } from 'modules/payroll_bundle/store/collections/paymentItems'
import type PaymentItem from 'modules/payroll_bundle/store/models/PaymentItem'

import { disablePaymentItem } from '../../_common/disableRowItem'
import StatCard from '../StatsCard'
import useGeneratePayment from '../useGeneratePayment'
import Table from './Table'

interface PaymentData {
  employee_id: number
  amount: number
  bank_account: string
}

const buildPaymentItems = createComputed((paymentItems) => {
  const parsed = paymentItems.map(
    (item: {
      employeeId: number
      amountInCents: number
      bankAccount: string | undefined | null
    }) => ({
      employee_id: item.employeeId,
      amount: item.amountInCents,
      bank_account: item.bankAccount,
    })
  )

  const values = parsed.map((item: PaymentData, index: number) => ({
    ...item,
    // the instantiation of PaymentItems collection requires an id
    id: index + 1,
  }))

  return new PaymentItems(values)
})

const GeneratePayments = () => {
  const payrollPayments = usePayrollPayments()
  const {
    paymentDispatch,
    selectedPayments,
    paymentConceptType: concept,
    paymentList,
  } = payrollPayments
  const [filterByFromStorage] = useLocalStorage<string>(LOCAL_STORAGE_KEYS.payrollPaymentsFilters)
  const { currentPeriod } = usePolicyPeriods()
  const { data: paymentData } = useDraftPaymentItems(currentPeriod?.id, concept)

  const { returnTo } = usePayrollBundlePageContext()

  const navigate = useNavigate()

  useUnsafeEffect(() => {
    return () => {
      paymentDispatch({
        type: RESET_DATA,
      })
    }
  }, [])

  const filtersForm = useLazyRef<FiltersForm>(
    () =>
      new FiltersForm({
        sortAttribute: 'first_name',
        sortDirection: 'asc',
        filterBy: filterByFromStorage,
      })
  ).current

  const itemList = buildPaymentItems(paymentData).get()

  const paymentItems = orderItems(itemList.models, filtersForm)

  const canBeSelected = (filteredPayments?: PaymentItem[]): number[] => {
    const payments = filteredPayments || paymentItems
    const selectables = payments.filter((payment) => {
      const user = getUserByItem(payment)

      return !disablePaymentItem(payment, user)
    })

    return selectables.map((e) => e.id)
  }

  const onSelectAll = (filteredPayments: PaymentItem[]) => {
    !selectedPayments.size ? selectAll(filteredPayments) : setSelectedPayments([])
  }

  const selectAll = (filteredPayments: PaymentItem[]) => {
    const ids = canBeSelected(filteredPayments)
    setSelectedPayments(ids)
  }

  const setSelectedPayments = (selectedPayments: number[]) => {
    paymentDispatch({
      type: UPDATE_DATA,
      payload: {
        selectedPayments: new Set(selectedPayments),
        paymentList: paymentItems,
      },
    })
  }

  const onSelectPaymentItem = (paymentItem: PaymentItem) => {
    const id = paymentItem.get('id')
    selectedPayments.has(id) ? onUnselect(id) : onSelect(id)
  }

  const onSelect = (userId: number) => {
    selectedPayments.add(userId)
    setSelectedPayments([...selectedPayments])
  }

  const onUnselect = (userId: number) => {
    selectedPayments.delete(userId)
    setSelectedPayments([...selectedPayments])
  }

  const selectedTotalAmount = Array.from(selectedPayments).reduce((totalAmount, id) => {
    const payment = paymentItems.find((payment) => payment.get('id') === id)
    const amount = payment?.get('amount')
    const user = payment && getUserByItem(payment)
    const disabled = payment ? disablePaymentItem(payment, user) : true

    return !disabled ? totalAmount + amountToNumber(amount) : totalAmount
  }, 0)

  const handleBack = () => {
    paymentDispatch({
      type: RESET_DATA,
    })

    if (returnTo) {
      navigate(returnTo)
    }
  }

  const {
    isDisabled: continueButtonIsDisabled,
    onClick: continueButtonOnClick,
    title: continueButtonLabel,
  } = useGeneratePayment({ selected: selectedPayments, builtPaymentItems: paymentList })

  return (
    <ResponsivePage.Body
      variant="full"
      showHeader={false}
      transactionalActions={{
        primary: {
          label: continueButtonLabel,
          onClick: continueButtonOnClick,
          disabled: continueButtonIsDisabled,
        },
        secondary: {
          label: i18n.t('common.back'),
          onClick: handleBack,
        },
      }}
    >
      <Box>
        <Box flexDirection="row" paddingX="s40" paddingY="s8" justifyContent="spaceBetween">
          <BlankSlate
            label={i18n.t(
              'payroll_bundle.results_payments.payments.generate_payment.generate_new_payment'
            )}
            illustration="common-file-text-upload"
            message={i18n.t(
              'payroll_bundle.results_payments.payments.generate_payment.description'
            )}
            withLine
            alignment="left"
          />

          <StatCard totalAmount={selectedTotalAmount} employeeCount={selectedPayments.size} />
        </Box>
        <Box padding="s12">
          <Table
            checkable
            onSelectAll={onSelectAll}
            selected={selectedPayments}
            selectable={canBeSelected()}
            onSelectItem={onSelectPaymentItem}
            paymentItems={paymentItems}
            filtersForm={filtersForm}
          />
        </Box>
      </Box>
    </ResponsivePage.Body>
  )
}

export default observer(GeneratePayments)
